import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { AffiliateCode, Referral, Payout } from '@/types';
import { ProtectedRoute } from '@/components/ProtectedRoute';

const pendingOf = (code: AffiliateCode) => (code.totalEarnings || 0) - (code.totalPaid || 0);

function AdminAffiliatesContent() {
  const [loading, setLoading] = useState(true);
  const [affiliateCodes, setAffiliateCodes] = useState<AffiliateCode[]>([]);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [stats, setStats] = useState({
    totalCodes: 0,
    totalReferrals: 0,
    totalEarnings: 0,
    totalPaid: 0,
    totalPending: 0,
  });

  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchAffiliateData();
  }, []);

  const fetchAffiliateData = async () => {
    try {
      setLoading(true);
      const codesQuery = query(collection(db, 'affiliateCodes'), orderBy('createdAt', 'desc'));
      const codesSnapshot = await getDocs(codesQuery);
      const codesData = codesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as AffiliateCode[];
      setAffiliateCodes(codesData);

      const referralsQuery = query(collection(db, 'referrals'), orderBy('createdAt', 'desc'));
      const referralsSnapshot = await getDocs(referralsQuery);
      const referralsData = referralsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Referral[];
      setReferrals(referralsData);

      const payoutsQuery = query(collection(db, 'payouts'), orderBy('paidAt', 'desc'));
      const payoutsSnapshot = await getDocs(payoutsQuery);
      const payoutsData = payoutsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Payout[];
      setPayouts(payoutsData);

      const totalReferrals = codesData.reduce((sum, c) => sum + (c.totalReferrals || 0), 0);
      const totalEarnings = codesData.reduce((sum, c) => sum + (c.totalEarnings || 0), 0);
      const totalPaid = codesData.reduce((sum, c) => sum + (c.totalPaid || 0), 0);

      setStats({
        totalCodes: codesData.length,
        totalReferrals,
        totalEarnings,
        totalPaid,
        totalPending: totalEarnings - totalPaid,
      });
    } catch (err) {
      console.error('Error fetching affiliate data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayout = async (payout: Payout) => {
    if (!confirm(`Delete the $${(payout.amount || 0).toFixed(2)} payout to ${payout.affiliateCode}? This rolls back their paid total.`)) return;
    setActionError(null);
    try {
      const deleteFn = httpsCallable(functions, 'adminDeletePayout');
      await deleteFn({ payoutId: payout.id });
      setActionSuccess('Payout deleted');
      await fetchAffiliateData();
    } catch (err: any) {
      setActionError(err.message || 'Failed to delete payout');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <LoadingSpinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Admin Nav */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', borderBottom: '1px solid var(--border)', paddingBottom: '0' }}>
          <a
            href="/admin/tickets"
            style={{ padding: '0.5rem 1rem', color: 'var(--muted)', textDecoration: 'none', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.1em', borderBottom: '2px solid transparent', marginBottom: '-1px' }}
          >
            Tickets
          </a>
          <a
            href="/admin/affiliates"
            style={{ padding: '0.5rem 1rem', color: 'var(--gold)', textDecoration: 'none', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.1em', borderBottom: '2px solid var(--gold)', marginBottom: '-1px' }}
          >
            Affiliates
          </a>
        </div>

        {/* Stats */}
        <div className="terminal-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', marginBottom: '2rem' }}>
          <div className="terminal-card">
            <div className="card-header"><span className="card-num">S1</span> CODES</div>
            <div className="card-body" style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--gold)' }}>{stats.totalCodes}</div>
          </div>
          <div className="terminal-card">
            <div className="card-header"><span className="card-num">S2</span> REFERRALS</div>
            <div className="card-body" style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--gold)' }}>{stats.totalReferrals}</div>
          </div>
          <div className="terminal-card">
            <div className="card-header"><span className="card-num">S3</span> EARNED</div>
            <div className="card-body" style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--success)' }}>${stats.totalEarnings.toFixed(2)}</div>
          </div>
          <div className="terminal-card">
            <div className="card-header"><span className="card-num">S4</span> PAID</div>
            <div className="card-body" style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--muted)' }}>${stats.totalPaid.toFixed(2)}</div>
          </div>
          <div className="terminal-card">
            <div className="card-header"><span className="card-num">S5</span> PENDING</div>
            <div className="card-body" style={{ fontSize: '2rem', fontWeight: 700, color: stats.totalPending > 0 ? 'var(--gold)' : 'var(--muted)' }}>${stats.totalPending.toFixed(2)}</div>
          </div>
        </div>

        {/* Feedback */}
        {actionSuccess && (
          <div className="terminal-alert success" style={{ marginBottom: '1rem' }}>
            <span>{actionSuccess}</span>
            <button onClick={() => setActionSuccess(null)} style={{ background: 'none', border: 'none', color: 'var(--success)', cursor: 'pointer', marginLeft: 'auto' }}>✕</button>
          </div>
        )}
        {actionError && (
          <div className="terminal-alert error" style={{ marginBottom: '1rem' }}>
            <span>{actionError}</span>
            <button onClick={() => setActionError(null)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', marginLeft: 'auto' }}>✕</button>
          </div>
        )}

        {/* Affiliate Codes Table */}
        <div className="terminal-card" style={{ marginBottom: '2rem' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><span className="card-num">T1</span> AFFILIATE CODES</span>
            <span style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>{affiliateCodes.length} TOTAL · CLICK TO MANAGE</span>
          </div>
          <div className="table-wrap">
            <table className="terminal-table">
              <thead>
                <tr>
                  <th>CODE</th>
                  <th>USER</th>
                  <th>X USERNAME</th>
                  <th>REFERRALS</th>
                  <th>EARNED</th>
                  <th>PAID</th>
                  <th>PENDING</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {affiliateCodes.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', color: 'var(--muted)', padding: '2rem' }}>NO AFFILIATE CODES YET</td>
                  </tr>
                ) : (
                  affiliateCodes.map((code) => {
                    const pending = pendingOf(code);
                    return (
                    <tr key={code.id}>
                      <td>
                        <Link to={`/admin/affiliates/${code.id}`} style={{ fontFamily: 'Consolas, monospace', color: 'var(--gold)', fontWeight: 700, textDecoration: 'none' }}>
                          {code.code}
                        </Link>
                      </td>
                      <td style={{ color: 'var(--fg)' }}>{code.userEmail}</td>
                      <td>
                        <a
                          href={`https://twitter.com/${code.xUsername}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'var(--gold)', textDecoration: 'none' }}
                        >
                          @{code.xUsername}
                        </a>
                      </td>
                      <td><span className="term-badge">{code.totalReferrals}</span></td>
                      <td style={{ color: 'var(--success)', fontWeight: 700 }}>${(code.totalEarnings || 0).toFixed(2)}</td>
                      <td style={{ color: 'var(--muted)' }}>${(code.totalPaid || 0).toFixed(2)}</td>
                      <td style={{ color: pending > 0 ? 'var(--gold)' : 'var(--muted)', fontWeight: 700 }}>${pending.toFixed(2)}</td>
                      <td>
                        <Link to={`/admin/affiliates/${code.id}`} className="action-btn" style={{ padding: '0.25rem 0.625rem', fontSize: '0.7rem', textDecoration: 'none' }}>
                          MANAGE →
                        </Link>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Referrals */}
        <div className="terminal-card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><span className="card-num">T2</span> RECENT REFERRALS</span>
            <span style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>{referrals.length} TOTAL</span>
          </div>
          <div className="card-body">
            {referrals.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--muted)', padding: '2rem 0' }}>NO REFERRALS YET</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {referrals.slice(0, 10).map((referral) => {
                  const affiliate = affiliateCodes.find(c => c.userId === referral.affiliateUserId);
                  return (
                    <div
                      key={referral.id}
                      className="message-item"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                          <span style={{ fontFamily: 'Consolas, monospace', color: 'var(--gold)', fontWeight: 700 }}>{referral.affiliateCode}</span>
                          <span style={{ color: 'var(--border)' }}>→</span>
                          <span style={{ color: 'var(--fg)' }}>{referral.referredEmail}</span>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Referred by: {affiliate?.userEmail || 'Unknown'}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: referral.earningsAmount ? 'var(--success)' : 'var(--muted)', fontWeight: 700, marginBottom: '0.25rem' }}>
                          {referral.earningsAmount ? `+$${referral.earningsAmount.toFixed(2)}` : '—'}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{referral.createdAt?.toDate().toLocaleDateString()}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Recent Payouts */}
        <div className="terminal-card" style={{ marginTop: '2rem' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><span className="card-num">T3</span> RECENT PAYOUTS</span>
            <span style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>{payouts.length} TOTAL</span>
          </div>
          <div className="card-body">
            {payouts.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--muted)', padding: '2rem 0' }}>NO PAYOUTS RECORDED YET</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {payouts.slice(0, 15).map((payout) => (
                  <div
                    key={payout.id}
                    className="message-item"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                        <span style={{ fontFamily: 'Consolas, monospace', color: 'var(--gold)', fontWeight: 700 }}>{payout.affiliateCode}</span>
                        {payout.txHash && (
                          <span style={{ fontSize: '0.7rem', color: 'var(--muted)', fontFamily: 'Consolas, monospace', wordBreak: 'break-all' }}>tx: {payout.txHash}</span>
                        )}
                      </div>
                      {payout.note && <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{payout.note}</div>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: 'var(--success)', fontWeight: 700, marginBottom: '0.25rem' }}>${(payout.amount || 0).toFixed(2)}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{payout.paidAt?.toDate().toLocaleDateString()}</div>
                      </div>
                      <button
                        onClick={() => handleDeletePayout(payout)}
                        className="action-btn secondary"
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem' }}
                        title="Delete payout"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>
    </Layout>
  );
}

export function AdminAffiliatesPage() {
  return (
    <ProtectedRoute requireAdmin>
      <AdminAffiliatesContent />
    </ProtectedRoute>
  );
}
